import React, { ReactElement } from 'react';
import { ReactComponent as DatadogIcon } from '@elementary/common-ui/assets/24x24/datadog.svg';
import { ReactComponent as SplunkIcon } from '@elementary/common-ui/assets/24x24/splunk.svg';
import { ReactComponent as GoogleCloudIcon } from '@elementary/common-ui/assets/24x24/google-cloud.svg';
import { ReactComponent as S3Icon } from '@elementary/common-ui/assets/24x24/s3.svg';
import { DefaultValues } from 'react-hook-form';
import {
  ActivityLogsIntegration,
  DatadogActivityLogsIntegrationProperties,
  ActivityLogsIntegrationType,
  CreateActivityLogsIntegrationApi,
  DatadogActivityLogsIntegrationPropertiesApi,
  ExternalAccountCredentialJson,
  GoogleCloudKeyFileJson,
  GcsActivityLogsIntegrationProperties,
  GcsActivityLogsIntegrationPropertiesApi,
  S3AccessKeyAuthApi,
  S3ActivityLogsIntegrationProperties,
  S3ActivityLogsIntegrationPropertiesApi,
  S3RoleAuthApi,
  SplunkActivityLogsIntegrationProperties,
  SplunkActivityLogsIntegrationPropertiesApi,
  UpdateActivityLogsIntegrationApi,
  UpdateS3AccessKeyAuthApi,
  UpdateS3RoleAuthApi,
} from 'shared/types';
import yaml from 'js-yaml';
import { clearSecretIndication } from 'shared/utils/secretUtils';
import DatadogActivityLogsForm from './DatadogActivityLogsForm';
import GcsActivityLogsForm from './GcsActivityLogsForm';
import S3ActivityLogsForm from './S3ActivityLogsForm';
import SplunkActivityLogsForm from './SplunkActivityLogsForm';

export type TagPair = { key: string; value: string };
export type FileUploadValue = { name: string; content: string } | File | null;

export type DatadogFormProperties = {
  integration_type: 'datadog';
  api_key: string | boolean;
  site: string;
  source: string;
  service: string;
  tags: TagPair[];
};

export type SplunkFormProperties = {
  integration_type: 'splunk';
  hec_url: string;
  token: string | boolean;
  index?: string;
};

export type GcsFormProperties = {
  integration_type: 'gcs';
  bucket_name: string;
  location?: string;
  keyfile?: string;
  keyfile_json?: FileUploadValue;
  external_account_json?: FileUploadValue;
  external_account_file?: string;
  max_file_size?: string;
};

type S3FormRoleAuth = {
  auth_type: 'role';
  role_arn: string;
  external_id?: string;
};

type S3FormAccessKeyAuth = {
  auth_type: 'access_key';
  access_key_id: string;
  secret_access_key: string | boolean;
};

export type S3FormProperties = {
  integration_type: 's3';
  bucket_name: string;
  region: string;
  auth: S3FormRoleAuth | S3FormAccessKeyAuth;
  use_privatelink: boolean;
  max_file_size?: string;
};

type ActivityLogsIntegrationFormBase = {
  id?: string;
  name: string;
};

export type DatadogActivityLogsIntegrationFormValues =
  ActivityLogsIntegrationFormBase & {
    integration_type: 'datadog';
    properties: DatadogFormProperties;
  };

export type SplunkActivityLogsIntegrationFormValues =
  ActivityLogsIntegrationFormBase & {
    integration_type: 'splunk';
    properties: SplunkFormProperties;
  };

export type GcsActivityLogsIntegrationFormValues =
  ActivityLogsIntegrationFormBase & {
    integration_type: 'gcs';
    properties: GcsFormProperties;
  };

export type S3ActivityLogsIntegrationFormValues =
  ActivityLogsIntegrationFormBase & {
    integration_type: 's3';
    properties: S3FormProperties;
  };

export type ActivityLogsIntegrationFormValues =
  | DatadogActivityLogsIntegrationFormValues
  | SplunkActivityLogsIntegrationFormValues
  | GcsActivityLogsIntegrationFormValues
  | S3ActivityLogsIntegrationFormValues;

export const ACTIVITY_LOGS_INTEGRATION_TYPES: ActivityLogsIntegrationType[] = [
  'datadog',
  'gcs',
  's3',
  'splunk',
];

const tagsObjectToPairs = (
  tags: Record<string, string> | null | undefined,
): TagPair[] =>
  Object.entries(tags || {}).map(([key, value]) => ({ key, value }));

const pairsToTagsObject = (pairs: TagPair[]) => {
  const keys = pairs.map((p) => (p.key || '').trim()).filter(Boolean);
  const hasDuplicateKeys = new Set(keys).size !== keys.length;
  if (hasDuplicateKeys) {
    throw new Error('Tag keys must be unique');
  }

  const obj = pairs.reduce<Record<string, string>>((acc, p) => {
    const k = (p.key || '').trim();
    const v = (p.value || '').trim();
    if (!k || !v) return acc;
    acc[k] = v;
    return acc;
  }, {});

  return Object.keys(obj).length ? obj : undefined;
};

export const getActivityLogsIntegrationLabel = (
  type: ActivityLogsIntegrationType,
): string => {
  switch (type) {
    case 'datadog':
      return 'Datadog';
    case 'gcs':
      return 'Google Cloud Storage';
    case 's3':
      return 'Amazon S3';
    case 'splunk':
      return 'Splunk';
    default:
      return type;
  }
};

export const getActivityLogsIntegrationIcon = (
  type: ActivityLogsIntegrationType,
): ReactElement => {
  switch (type) {
    case 'datadog':
      return <DatadogIcon />;
    case 'gcs':
      return <GoogleCloudIcon />;
    case 's3':
      return <S3Icon />;
    case 'splunk':
      return <SplunkIcon />;
    default:
      return <></>;
  }
};

export const ActivityLogsIntegrationFormContent = ({
  type,
}: {
  type: ActivityLogsIntegrationType;
}) => {
  switch (type) {
    case 'datadog':
      return <DatadogActivityLogsForm />;
    case 'gcs':
      return <GcsActivityLogsForm />;
    case 's3':
      return <S3ActivityLogsForm />;
    case 'splunk':
      return <SplunkActivityLogsForm />;
    default:
      return <></>;
  }
};

export const getActivityLogsIntegrationDefaultValues = ({
  data,
  preferredType,
}: {
  data?: ActivityLogsIntegration;
  preferredType?: ActivityLogsIntegrationType;
}): DefaultValues<ActivityLogsIntegrationFormValues> => {
  const type = (data?.type as ActivityLogsIntegrationType) || preferredType;

  switch (type) {
    case 'datadog': {
      if (data?.id && data?.properties) {
        const props =
          data.properties as DatadogActivityLogsIntegrationProperties;
        return {
          id: data.id,
          name: data.name,
          integration_type: 'datadog',
          properties: {
            integration_type: 'datadog',
            api_key: props.api_key,
            site: props.site,
            source: props.source,
            service: props.service,
            tags: tagsObjectToPairs(props.tags),
          },
        };
      }

      return {
        name: '',
        integration_type: 'datadog',
        properties: {
          integration_type: 'datadog',
          api_key: '',
          site: 'datadoghq.com',
          source: 'elementary-cloud',
          service: 'elementary',
          tags: [],
        },
      };
    }
    case 'splunk': {
      if (data?.id && data?.properties) {
        const props =
          data.properties as SplunkActivityLogsIntegrationProperties;
        return {
          id: data.id,
          name: data.name,
          integration_type: 'splunk',
          properties: {
            integration_type: 'splunk',
            hec_url: props.hec_url,
            token: props.token,
            index: props.index || '',
          },
        };
      }

      return {
        name: '',
        integration_type: 'splunk',
        properties: {
          integration_type: 'splunk',
          hec_url: '',
          token: '',
          index: '',
        },
      };
    }
    case 'gcs': {
      if (data?.id && data?.properties) {
        const props = data.properties as GcsActivityLogsIntegrationProperties;
        const keyfileValue: FileUploadValue =
          props.keyfile_json && typeof props.keyfile_json === 'object'
            ? {
                name: props.keyfile || 'keyfile.json',
                content: JSON.stringify(props.keyfile_json),
              }
            : props.keyfile_json
            ? { name: props.keyfile || 'keyfile.json', content: '' }
            : null;

        const wifFileValue: FileUploadValue =
          props.external_account_json &&
          typeof props.external_account_json === 'object'
            ? {
                name:
                  props.external_account_file ||
                  'clientLibraryConfig-elementary-aws.json',
                content: JSON.stringify(props.external_account_json),
              }
            : props.external_account_json
            ? {
                name:
                  props.external_account_file ||
                  'clientLibraryConfig-elementary-aws.json',
                content: '',
              }
            : null;

        return {
          id: data.id,
          name: data.name,
          integration_type: 'gcs',
          properties: {
            integration_type: 'gcs',
            bucket_name: props.bucket_name,
            location: props.location || '',
            keyfile: props.keyfile || '',
            keyfile_json: keyfileValue,
            external_account_json: wifFileValue,
            external_account_file: props.external_account_file || '',
            max_file_size:
              props.max_file_size != null ? String(props.max_file_size) : '',
          },
        };
      }

      return {
        name: '',
        integration_type: 'gcs',
        properties: {
          integration_type: 'gcs',
          bucket_name: '',
          location: '',
          keyfile: '',
          keyfile_json: null,
          external_account_json: null,
          external_account_file: '',
          max_file_size: '',
        },
      };
    }
    case 's3': {
      if (data?.id && data?.properties) {
        const props = data.properties as S3ActivityLogsIntegrationProperties;
        const auth: S3FormProperties['auth'] =
          props.auth.auth_type === 'role'
            ? {
                auth_type: 'role',
                role_arn: props.auth.role_arn,
                external_id: props.auth.external_id || '',
              }
            : {
                auth_type: 'access_key',
                access_key_id: props.auth.access_key_id,
                secret_access_key: props.auth.secret_access_key,
              };

        return {
          id: data.id,
          name: data.name,
          integration_type: 's3',
          properties: {
            integration_type: 's3',
            bucket_name: props.bucket_name,
            region: props.region,
            auth,
            use_privatelink: !!props.use_privatelink,
            max_file_size:
              props.max_file_size != null ? String(props.max_file_size) : '',
          },
        };
      }

      return {
        name: '',
        integration_type: 's3',
        properties: {
          integration_type: 's3',
          bucket_name: '',
          region: '',
          auth: {
            auth_type: 'access_key',
            access_key_id: '',
            secret_access_key: '',
          },
          use_privatelink: false,
          max_file_size: '',
        },
      };
    }
    default: {
      return {};
    }
  }
};

const parseUploadedJsonFile = <T,>(
  fileValue: FileUploadValue,
  errorMessage: string,
): T => {
  if (!fileValue || fileValue instanceof File) {
    throw new Error(errorMessage);
  }
  const content = (fileValue.content || '').trim();
  if (!content) {
    throw new Error(errorMessage);
  }
  return yaml.load(content) as T;
};

const parseKeyfileJson = (fileValue: FileUploadValue): GoogleCloudKeyFileJson =>
  parseUploadedJsonFile<GoogleCloudKeyFileJson>(
    fileValue,
    'Service account file is required',
  );

const parseExternalAccountJson = (
  fileValue: FileUploadValue,
): ExternalAccountCredentialJson =>
  parseUploadedJsonFile<ExternalAccountCredentialJson>(
    fileValue,
    'WIF credential file is required',
  );

export const toCreateActivityLogsIntegrationPayload = (
  values: ActivityLogsIntegrationFormValues,
): CreateActivityLogsIntegrationApi => {
  switch (values.integration_type) {
    case 'datadog': {
      const props = values.properties;
      const properties: DatadogActivityLogsIntegrationPropertiesApi = {
        integration_type: 'datadog',
        api_key: props.api_key as string,
        site: props.site.trim(),
        source: props.source.trim(),
        service: props.service.trim(),
        tags: pairsToTagsObject(props.tags),
      };

      return {
        name: values.name.trim(),
        enabled: true,
        properties,
      };
    }
    case 'splunk': {
      const props = values.properties;
      const index = (props.index || '').trim();
      const properties: SplunkActivityLogsIntegrationPropertiesApi = {
        integration_type: 'splunk',
        hec_url: props.hec_url.trim(),
        token: props.token as string,
        ...(index ? { index } : {}),
      };

      return {
        name: values.name.trim(),
        enabled: true,
        properties,
      };
    }
    case 'gcs': {
      const props = values.properties;
      const isWif =
        props.external_account_json != null &&
        !(props.external_account_json instanceof File) &&
        typeof props.external_account_json === 'object' &&
        (props.external_account_json.content || '').trim().length > 0;

      let credentialFields: Partial<GcsActivityLogsIntegrationPropertiesApi>;
      if (isWif) {
        const external_account_json = parseExternalAccountJson(
          props.external_account_json!,
        );
        const wifFileName =
          props.external_account_json &&
          !(props.external_account_json instanceof File)
            ? props.external_account_json.name
            : 'clientLibraryConfig-elementary-aws.json';
        credentialFields = {
          external_account_json,
          external_account_file:
            wifFileName.trim() || 'clientLibraryConfig-elementary-aws.json',
        };
      } else {
        const keyfile_json = parseKeyfileJson(props.keyfile_json!);
        const keyfileName =
          props.keyfile_json && !(props.keyfile_json instanceof File)
            ? props.keyfile_json.name
            : 'keyfile.json';
        credentialFields = {
          keyfile_json,
          keyfile: (props.keyfile || keyfileName).trim() || 'keyfile.json',
        };
      }

      const properties: GcsActivityLogsIntegrationPropertiesApi = {
        integration_type: 'gcs',
        bucket_name: props.bucket_name.trim(),
        ...credentialFields,
        ...(props.max_file_size?.trim()
          ? { max_file_size: props.max_file_size.trim() }
          : {}),
        ...(props.location?.trim() ? { location: props.location.trim() } : {}),
      };

      return {
        name: values.name.trim(),
        enabled: true,
        properties,
      };
    }
    case 's3': {
      const props = values.properties;
      const auth: S3RoleAuthApi | S3AccessKeyAuthApi =
        props.auth.auth_type === 'role'
          ? {
              auth_type: 'role',
              role_arn: props.auth.role_arn.trim(),
              ...(props.auth.external_id?.trim()
                ? { external_id: props.auth.external_id.trim() }
                : {}),
            }
          : {
              auth_type: 'access_key',
              access_key_id: props.auth.access_key_id.trim(),
              secret_access_key: props.auth.secret_access_key as string,
            };

      const properties: S3ActivityLogsIntegrationPropertiesApi = {
        integration_type: 's3',
        bucket_name: props.bucket_name.trim(),
        region: props.region.trim(),
        auth,
        use_privatelink: !!props.use_privatelink,
        ...(props.max_file_size?.trim()
          ? { max_file_size: props.max_file_size.trim() }
          : {}),
      };

      return {
        name: values.name.trim(),
        enabled: true,
        properties,
      };
    }
    default:
      throw new Error('Unsupported integration type');
  }
};

export const toUpdateActivityLogsIntegrationPayload = (
  values: ActivityLogsIntegrationFormValues,
): UpdateActivityLogsIntegrationApi => {
  switch (values.integration_type) {
    case 'datadog': {
      const props = values.properties;
      const tags = pairsToTagsObject(props.tags);
      const apiKey = clearSecretIndication(props.api_key);

      const properties: UpdateActivityLogsIntegrationApi['properties'] = {
        integration_type: 'datadog',
        site: props.site?.trim(),
        source: props.source?.trim(),
        service: props.service?.trim(),
        tags,
        ...(apiKey ? { api_key: apiKey } : {}),
      };

      return {
        name: values.name?.trim(),
        properties,
      };
    }
    case 'splunk': {
      const props = values.properties;
      const token = clearSecretIndication(props.token);
      const index = (props.index || '').trim();

      const properties: UpdateActivityLogsIntegrationApi['properties'] = {
        integration_type: 'splunk',
        hec_url: props.hec_url?.trim(),
        ...(index ? { index } : { index: null }),
        ...(token ? { token } : {}),
      };

      return {
        name: values.name?.trim(),
        properties,
      };
    }
    case 'gcs': {
      const props = values.properties;

      const shouldUpdateKeyfile =
        props.keyfile_json &&
        !(props.keyfile_json instanceof File) &&
        typeof props.keyfile_json === 'object' &&
        (props.keyfile_json.content || '').trim().length > 0;

      const shouldUpdateWif =
        props.external_account_json &&
        !(props.external_account_json instanceof File) &&
        typeof props.external_account_json === 'object' &&
        (props.external_account_json.content || '').trim().length > 0;

      const keyfile_json = shouldUpdateKeyfile
        ? parseKeyfileJson(props.keyfile_json!)
        : undefined;

      const external_account_json = shouldUpdateWif
        ? parseExternalAccountJson(props.external_account_json!)
        : undefined;

      const keyfileName =
        props.keyfile_json && !(props.keyfile_json instanceof File)
          ? props.keyfile_json.name
          : undefined;

      const wifFileName =
        props.external_account_json &&
        !(props.external_account_json instanceof File)
          ? props.external_account_json.name
          : undefined;

      const properties: UpdateActivityLogsIntegrationApi['properties'] = {
        integration_type: 'gcs',
        bucket_name: props.bucket_name?.trim(),
        ...(props.keyfile?.trim()
          ? { keyfile: props.keyfile.trim() }
          : keyfileName
          ? { keyfile: keyfileName.trim() }
          : {}),
        ...(wifFileName ? { external_account_file: wifFileName.trim() } : {}),
        ...(props.location?.trim()
          ? { location: props.location.trim() }
          : { location: null }),
        ...(props.max_file_size?.trim()
          ? { max_file_size: props.max_file_size.trim() }
          : { max_file_size: null }),
        ...(keyfile_json ? { keyfile_json } : {}),
        ...(external_account_json ? { external_account_json } : {}),
      };

      return {
        name: values.name?.trim(),
        properties,
      };
    }
    case 's3': {
      const props = values.properties;
      const auth: UpdateS3RoleAuthApi | UpdateS3AccessKeyAuthApi =
        props.auth.auth_type === 'role'
          ? {
              auth_type: 'role',
              role_arn: props.auth.role_arn?.trim(),
              external_id: props.auth.external_id?.trim() || null,
            }
          : {
              auth_type: 'access_key',
              access_key_id: props.auth.access_key_id?.trim(),
              ...(clearSecretIndication(props.auth.secret_access_key)
                ? {
                    secret_access_key: clearSecretIndication(
                      props.auth.secret_access_key,
                    ),
                  }
                : {}),
            };

      const properties: UpdateActivityLogsIntegrationApi['properties'] = {
        integration_type: 's3',
        bucket_name: props.bucket_name?.trim(),
        region: props.region?.trim(),
        auth,
        use_privatelink: !!props.use_privatelink,
        ...(props.max_file_size?.trim()
          ? { max_file_size: props.max_file_size.trim() }
          : { max_file_size: null }),
      };

      return {
        name: values.name?.trim(),
        properties,
      };
    }
    default:
      throw new Error('Unsupported integration type');
  }
};
